import React, { useState, useEffect } from 'react'
import {
  Download,
  Trash2,
  RotateCcw,
  CreditCard,
  Receipt,
  AlertCircle,
  CheckCircle,
  Eye,
  EyeOff,
  RefreshCw,
  Edit
} from 'lucide-react'
import { receiptAPI } from '../../api/insuranceAPI'
import { getPatient } from '../../api/insuranceAPI'
import ReceiptEditor from './ReceiptEditor'
import apiClient from '../../api/axiosClient'
import { useAuth } from '../../contexts/AuthContext'

const PatientBilling = ({ patientId }) => {
  const { user } = useAuth()
  const [receipts, setReceipts] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [summary, setSummary] = useState(null)
  const [pagination, setPagination] = useState(null)
  const [showDeleted, setShowDeleted] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [editingReceipt, setEditingReceipt] = useState(null)
  const [patient, setPatient] = useState(null)

  // Load receipts
  const loadReceipts = async () => {
    if (!patientId) return
    
    setLoading(true)
    setError(null)
    
    try {
      const params = {
        page: currentPage,
        per_page: 10,
        include_deleted: showDeleted
      }
      
      const response = await receiptAPI.getPatientReceipts(patientId, params)
      const data = response.data
      
      setReceipts((data.receipts || []).filter(receipt => !receipt.narration?.includes('IP FINAL BILL')))
      setSummary(data.summary || {})
      setPagination(data.pagination || {})
      
      // Load patient data if not already loaded
      if (!patient && data.patient) {
        setPatient(data.patient)
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load receipts')
    } finally {
      setLoading(false)
    }
  }

  // Load patient data
  const loadPatientData = async () => {
    if (!patientId || patient) return
    
    try {
      const response = await getPatient(patientId)
      setPatient(response.data.data)
    } catch (err) {
      console.error('Failed to load patient data:', err)
    }
  }

  // Edit receipt
  const handleEditReceipt = async (receipt) => {
    console.log('Edit clicked for receipt:', receipt)
    console.log('Current patient:', patient)
    
    // Load patient data if not available
    if (!patient) {
      await loadPatientData()
    }
    
    setEditingReceipt(receipt)
  }

  // Save edited receipt
  const handleSaveReceipt = async (receiptId, updateData) => {
    try {
      await receiptAPI.updateReceipt(receiptId, updateData)
      loadReceipts()
    } catch (err) {
      throw err // Let ReceiptEditor handle the error
    }
  }

  // Delete receipt
  const handleDeleteReceipt = async (receiptId, receiptNo) => {
    if (!window.confirm(`Are you sure you want to delete receipt ${receiptNo}?`)) return
    
    try {
      await receiptAPI.deleteReceipt(receiptId)
      loadReceipts()
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to delete receipt')
    }
  }

  // Restore receipt
  const handleRestoreReceipt = async (receiptId, receiptNo) => {
    if (!window.confirm(`Are you sure you want to restore receipt ${receiptNo}?`)) return
    
    try {
      await receiptAPI.restoreReceipt(receiptId)
      loadReceipts()
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to restore receipt')
    }
  }

  // Download receipt
  const handleDownloadReceipt = async (receiptId, receiptNo, receiptDate) => {
    try {
      const blob = await receiptAPI.downloadReceipt(receiptId)
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `Receipt_${receiptNo}_${receiptDate.replace(/-/g, '')}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to download receipt')
    }
  }

  // View receipt in new tab
  const handleViewReceipt = async (receiptId) => {
    try {
      const blob = await receiptAPI.downloadReceipt(receiptId)
      const url = window.URL.createObjectURL(blob)
      window.open(url, '_blank')
      // Clean up after a delay to ensure the PDF loads
      setTimeout(() => window.URL.revokeObjectURL(url), 1000)

      const receipt = receipts.find(item => item.receipt_id === receiptId)
      if (user?.role === 'super_admin' && receipt?.narration?.includes('IP FINAL BILL')) {
        await apiClient.post(`/insurance/ip-final-bills/${receiptId}/viewed`)
        setReceipts(prev => prev.map(item => (
          item.receipt_id === receiptId
            ? { ...item, viewed_by_current_user: true }
            : item
        )))
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to view receipt')
    }
  }

  useEffect(() => {
    loadReceipts()
  }, [patientId, showDeleted, currentPage])

  if (!patientId) {
    return (
      <div className="text-center py-8">
        <CreditCard className="mx-auto text-gray-400 mb-4" size={48} />
        <p className="text-gray-600">No patient selected</p>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      {/* Controls */}
      <div className="flex items-center justify-end">
        <button
          onClick={loadReceipts}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
          <AlertCircle className="text-red-600 mr-3" size={20} />
          <span className="text-red-700">{error}</span>
          <button
            onClick={() => setError(null)}
            className="ml-auto text-red-600 hover:text-red-800 text-xl font-bold"
          >
            ×
          </button>
        </div>
      )}

      {/* Receipts Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Receipt No</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Date</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Amount</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Items</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-3"></div>
                      <span className="text-gray-600">Loading receipts...</span>
                    </div>
                  </td>
                </tr>
              ) : receipts.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center">
                    <Receipt className="mx-auto text-gray-400 mb-2" size={32} />
                    <p className="text-gray-600">No receipts found</p>
                  </td>
                </tr>
              ) : (
                receipts.map((receipt) => (
                  <tr key={receipt.receipt_id} className={`border-b border-gray-100 hover:bg-gray-50 ${
                    receipt.is_deleted ? 'bg-red-50' : ''
                  }`}>
                    <td className="px-4 py-3">
                      <span className="font-medium text-gray-900">{receipt.receipt_no}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm">
                        <div className="text-gray-900">{receipt.receipt_date}</div>
                        <div className="text-gray-500">{receipt.receipt_time}</div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-semibold text-gray-900">{receipt.total_amount?.toFixed(2)}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600">{receipt.items?.length || 0} item(s)</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleViewReceipt(receipt.receipt_id)}
                          disabled={user?.role === 'super_admin' && receipt.narration?.includes('IP FINAL BILL') && receipt.viewed_by_current_user}
                          className="p-1 text-purple-600 hover:bg-purple-100 rounded transition-colors disabled:text-gray-400 disabled:hover:bg-transparent disabled:cursor-not-allowed"
                          title={
                            user?.role === 'super_admin' && receipt.narration?.includes('IP FINAL BILL') && receipt.viewed_by_current_user
                              ? 'You have already viewed this bill. If you need to access it again, please contact the Insurance Manager'
                              : 'View PDF'
                          }
                        >
                          <Eye size={16} />
                        </button>

                        {user?.role === 'super_admin' && receipt.narration?.includes('IP FINAL BILL') && receipt.viewed_by_current_user && (
                          <span className="text-xs text-red-600">
                            You have already viewed this bill. If you need to access it again, please contact the Insurance Manager
                          </span>
                        )}
                        
                        {!(user?.role === 'super_admin' && receipt.narration?.includes('IP FINAL BILL')) && (
                          <button
                            type="button"
                            onClick={() => handleDownloadReceipt(receipt.receipt_id, receipt.receipt_no, receipt.receipt_date)}
                            className="p-1 text-blue-600 hover:bg-blue-100 rounded transition-colors"
                            title="Download PDF"
                          >
                            <Download size={16} />
                          </button>
                        )}
                        
                        {!(user?.role === 'super_admin' && receipt.narration?.includes('IP FINAL BILL')) && !receipt.is_deleted && new Date(receipt.receipt_date).toDateString() === new Date().toDateString() && (
                          <button
                            type="button"
                            onClick={() => handleEditReceipt(receipt)}
                            className="p-1 text-orange-600 hover:bg-orange-100 rounded transition-colors"
                            title="Edit Receipt"
                          >
                            <Edit size={16} />
                          </button>
                        )}
                        
                        {!(user?.role === 'super_admin' && receipt.narration?.includes('IP FINAL BILL')) && (receipt.is_deleted ? (
                          <button
                            type="button"
                            onClick={() => handleRestoreReceipt(receipt.receipt_id, receipt.receipt_no)}
                            className="p-1 text-green-600 hover:bg-green-100 rounded transition-colors"
                            title="Restore Receipt"
                          >
                            <RotateCcw size={16} />
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleDeleteReceipt(receipt.receipt_id, receipt.receipt_no)}
                            className="p-1 text-red-600 hover:bg-red-100 rounded transition-colors"
                            title="Delete Receipt"
                          >
                            <Trash2 size={16} />
                          </button>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        {pagination && pagination.total_pages > 1 && (
          <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing {((pagination.current_page - 1) * pagination.per_page) + 1} to {Math.min(pagination.current_page * pagination.per_page, pagination.total)} of {pagination.total} results
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(prev => prev - 1)}
                disabled={!pagination.has_prev}
                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Previous
              </button>
              
              <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded text-sm">
                {pagination.current_page} of {pagination.total_pages}
              </span>
              
              <button
                onClick={() => setCurrentPage(prev => prev + 1)}
                disabled={!pagination.has_next}
                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Receipt Editor Modal */}
      {editingReceipt && (
        patient ? (
          <ReceiptEditor
            receipt={editingReceipt}
            patient={patient}
            onClose={() => setEditingReceipt(null)}
            onSave={handleSaveReceipt}
          />
        ) : (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 flex items-center gap-3">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <span className="text-gray-700">Loading patient data...</span>
            </div>
          </div>
        )
      )}
    </div>
  )
}

export default PatientBilling
